import { useConfigContext } from "@/app";
import { DialogLocal } from "@/components/Dialog";
import { LINK_ACCOUNT_TOKEN, USER_TOKEN } from "@/constants";
import { useNotification } from "@/logic/notification";
import { selectorLocalStorage, useLocalStorage, useUserInfoStore } from "@/stores/userInfo";
import { UserInfo } from "@/types/Api/User/MyAccount/userInfo";
import { setLogoutMethods } from "@/utils/request";
import { AutoCenter } from "antd-mobile";
import { createContext, useContext } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
interface Context {
  user: UserInfo["response"];
  login: (user: UserInfo["response"]) => void;
  logout: () => void;
  token: string;
}
const AuthUserContext = createContext<Context | null>(null);

/* `export const AuthUserProvider` 正在导出一个为用户身份验证提供上下文的 React 组件。它接受一个 children 道具并返回一个
AuthUserContext.Provider 组件，该组件环绕着 children。 `AuthUserContext.Provider` 组件提供了一个包含`user` 状态、`login`
和`logout` 函数的值对象，子组件可以使用`useUserAuth` 挂钩访问这些函数。 `useLocalStorage` 挂钩用于将 `user` 状态保存在本地存储中。
`useNavigate` 挂钩用于在成功登录或注销后导航到不同的路由。 */
export const AuthUserProvider = ({
  children
}: {
  children: any;
}) => {
  const [notification, createNotificationPolling] = useNotification();
  const [user, setUser, clearUser] = useUserInfoStore()(state => [state.userInfo, state.setUserInfo, state.clearUserInfo]);
  const {
    updateBanks,
    updateUserPayments
  } = useConfigContext();
  const [token, setToken] = useLocalStorage<string>(USER_TOKEN)(selectorLocalStorage);
  const navigate = useNavigate();
  const login = async (user: UserInfo["response"]) => {
    // 写入用户信息
    setUser(user);
    // 获取银行卡列表
    updateBanks();
    // 获取用户支付方式列表
    updateUserPayments();
    // 跳转首页
    // 检查是否存在绑定token
    if (window.localStorage[LINK_ACCOUNT_TOKEN]) {
      navigate('/link_account');
    } else {
      navigate("/");
    }
    // 创建通知
    createNotificationPolling();
  };
  const logout = async () => {
    console.warn("run logout code");
    setToken("");
    clearUser();
    notification.clear();
    navigate("/auth/login");
  };
  setLogoutMethods(logout);
  return <AuthUserContext.Provider value={{
    user,
    login,
    logout,
    token
  }} data-sentry-element="unknown" data-sentry-component="AuthUserProvider" data-sentry-source-file="userAuth.tsx">
      {children}
    </AuthUserContext.Provider>;
};
export const useUserAuthContext = (): Context => {
  return (useContext(AuthUserContext) as Context);
};

/* `export const AuthUserRoute` 是一个 React 组件，用于检查用户是否已通过身份验证。如果用户通过身份验证，它会呈现子组件。如果用户未通过身份验证，它将使用来自
`react-router-dom` 的 `Navigate` 组件导航到登录页面。 */
export const AuthUserRoute = ({
  children
}: {
  children: any;
}) => {
  const {
    token,
    user
  } = useUserAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  if (children.type.name === "PagePayOrder" && user && user.isEnteredPassword === false) {
    //扫码 我的订单   未绑定资金密码
    DialogLocal({
      cancelText: "取消",
      content: <AutoCenter>请设置您的资金密码！</AutoCenter>,
      onConfirm() {
        navigate(`/user_pay_psw`);
      },
      onCancel: () => {
        navigate(`/`);
      }
    });
  }
  if (location.pathname === '/link_account' || location.pathname === '/auto_register') {
    if (searchParams.get('auth')) window.localStorage[LINK_ACCOUNT_TOKEN] = searchParams.get('auth');
  }
  if (!token) {
    if (location.pathname === '/auto_register') {
      return <Navigate to="/auth/login?auto_register=1" replace />;
    }
    return <Navigate to="/auth/login" replace />;
  } else {
    if (location.pathname === '/auto_register') {
      return <Navigate to="/" replace />;
    }
  }
  return children;
};

/* `export const UnauthUserRoute` 是一个 React 组件，用于检查用户是否未通过身份验证。如果用户未通过身份验证，它会呈现其子项。如果用户通过身份验证，它会使用来自
`react-router-dom` 的 `Navigate` 组件导航到主页。 */
export const UnauthUserRoute = ({
  children
}: {
  children: any;
}) => {
  const {
    token
  } = useUserAuthContext();
  if (token) {
    return <Navigate to="/" replace />;
  }
  return children;
};